import { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Link, TextField, Typography,
} from '@mui/material';
import { Send, CheckCircle, ContentCopy } from '@mui/icons-material';
import { sendQuoteToClient } from '../../../services/quoteService';
import type { QuoteDetail } from '../../../types/quote';

interface QuoteSendDialogProps {
  open: boolean;
  onClose: () => void;
  quote: QuoteDetail;
  onSent: () => void;
}

export const QuoteSendDialog = ({ open, onClose, quote, onSent }: QuoteSendDialogProps) => {
  const defaultSubject = `Quote ${quote.QuoteNumber} — ${quote.Title || 'Lab Testing Services'}`;
  const defaultBody = [
    'Dear Client,',
    '',
    `Please find attached Quote ${quote.QuoteNumber} for your review.`,
    '',
    'You can view the quote details and respond directly using the link provided in this email.',
    '',
    'If you have any questions, please do not hesitate to reach out.',
    '',
    'Best regards,',
    'BGC Engineering — Geotechnical Laboratory',
  ].join('\n');

  const [recipientEmails, setRecipientEmails] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptLink, setAcceptLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSend = async () => {
    const emails = recipientEmails.split(',').map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) {
      setError('Please enter at least one recipient email.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const result = await sendQuoteToClient(quote.QuoteKey, { recipientEmails: emails, subject, body });
      // Build the accept link from the returned token
      const link = `${window.location.origin}/quote/accept/${result.token}`;
      setAcceptLink(link);
      onSent();
    } catch {
      setError('Failed to send quote. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleCopy = () => {
    if (acceptLink) {
      navigator.clipboard.writeText(acceptLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setAcceptLink(null);
    setCopied(false);
    setError(null);
    onClose();
  };

  // Success state — show the accept link
  if (acceptLink) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle sx={{ color: 'success.main' }} />
          Quote Sent!
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The quote status has been updated to <strong>Sent</strong>.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Client Accept Link (Dry Run Mode)
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              SendGrid is not configured yet. Share this link with the client or open it yourself to preview the accept experience:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Link href={acceptLink} target="_blank" sx={{ fontSize: '0.8rem', wordBreak: 'break-all', flex: 1 }}>
                {acceptLink}
              </Link>
              <Button size="small" startIcon={<ContentCopy />} onClick={handleCopy}
                sx={{ textTransform: 'none', fontSize: '0.75rem', flexShrink: 0 }}>
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </Box>
          </Alert>
          <Button variant="outlined" size="small" href={acceptLink} target="_blank"
            sx={{ textTransform: 'none' }}>
            Open Accept Page
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Quote to Client</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        <TextField label="Recipient Email(s)" placeholder="client@example.com"
          value={recipientEmails} onChange={(e) => setRecipientEmails(e.target.value)}
          helperText="Separate multiple emails with commas" fullWidth autoFocus />
        <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} fullWidth />
        <TextField label="Message Body" value={body} onChange={(e) => setBody(e.target.value)}
          multiline rows={8} fullWidth />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={sending} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" startIcon={sending ? <CircularProgress size={16} /> : <Send />}
          onClick={handleSend} disabled={sending} sx={{ textTransform: 'none' }}>
          {sending ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
